/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { HolderOutlined } from '@ant-design/icons'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import colors from '@sportninja/common/constants/appColors'

/**
 * Sortable wiring for one row. Returned separately from the handle itself
 * because the row needs `setNodeRef`/`style` while only the grip listens for
 * the drag — dragging from anywhere else would swallow row clicks and cell
 * editing.
 */
export const useSortableRow = (id: string, disabled: boolean) => {
  const {
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    attributes,
    listeners,
    isDragging,
  } = useSortable({ id, disabled })

  return {
    setNodeRef,
    setActivatorNodeRef,
    attributes,
    listeners,
    isDragging,
    style: {
      transform: CSS.Translate.toString(transform),
      transition,
      opacity: isDragging ? 0.4 : undefined,
      // A dragged row must paint above its neighbours or it slides underneath.
      zIndex: isDragging ? 1 : undefined,
      position: isDragging ? ('relative' as const) : undefined,
    },
  }
}

interface DragHandleProps {
  disabled?: boolean
  label: string
  setActivatorNodeRef?: (element: HTMLElement | null) => void
  attributes?: Record<string, unknown>
  listeners?: Record<string, unknown>
}

/** The grip. Hidden from assistive tech's content flow but keyboard-reachable. */
const DragHandle = ({
  disabled = false,
  label,
  setActivatorNodeRef,
  attributes,
  listeners,
}: DragHandleProps) => {
  'use memo'

  if (disabled) {
    return <span css={placeholderStyle} aria-hidden />
  }

  return (
    <button
      type='button'
      ref={setActivatorNodeRef}
      css={handleStyle}
      aria-label={label}
      {...attributes}
      {...listeners}
    >
      <HolderOutlined />
    </button>
  )
}

/* Stretches to the row's full height and centres within it: the grip belongs
   to the whole row, not to the name line the badge and twisty sit on. */
const handleStyle = css`
  width: 10px;
  align-self: stretch;
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: 0;
  color: ${colors.NEUTRAL_100};
  cursor: grab;
  touch-action: none;

  &:hover {
    color: ${colors.NEUTRAL_0};
  }

  &:active {
    cursor: grabbing;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

const placeholderStyle = css`
  width: 10px;
  align-self: stretch;
  flex: none;
`

export default DragHandle
